// Clause detail pane: ancestor path, number and title (with its English rendering for a clause not in English) with kind, level and pages (priority, extraction status and quality flags for Operator and Admin), the clause text (TermText in the original, Original · English · Side by side when a translation exists), requirement cards (the English statement with the original wording quoted), children with requirement counts, figures, tables, references in both directions and unresolved references, and the clause in other versions of the grid code.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronRightIcon,
  FileTextIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  SquareDashedMousePointerIcon,
  Table2Icon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import type { ClauseDetailOut, FigureRow, ReferenceRef, Requirement, TableRow } from "@/api/__generated__";
import { clausesGetClauseOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { useInternalDetails } from "@/app/useInternalDetails";
import {
  BilingualText,
  hasTranslation,
  isEnglish,
  useBilingualMode,
} from "@/components/common/BilingualText";
import { ClauseLink } from "@/components/common/ClauseLink";
import { CopyButton } from "@/components/common/CopyButton";
import { EmptyState } from "@/components/common/EmptyState";
import { FlagBadge } from "@/components/common/FlagBadge";
import { ModalityBadge } from "@/components/common/ModalityBadge";
import { ProblemState } from "@/components/common/ProblemState";
import { RequirementLink } from "@/components/common/RequirementLink";
import { ValueTypeIcon } from "@/components/common/ValueTypeIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { ClauseVersions, hasHistory } from "@/features/library/ClauseVersions";
import { FigureDialog, figureTitle } from "@/features/library/FigureDialog";
import { pageRange, STATUS_DOT } from "@/features/library/format";
import { requirementValue } from "@/features/library/requirement-value";
import { TableView } from "@/features/library/TableView";
import { FigureImage } from "@/features/source/FigureImage";
import { TermText } from "@/features/vocabulary/TermText";
import { cn } from "@/lib/cn";
import { formatCompact, formatNumber, plural } from "@/lib/format";
import { EXTRACTION_STATUS_LABELS } from "@/lib/labels";
import { absoluteUrl } from "@/lib/url";

interface ClauseDetailProps {
  docUid: string;
  region: string;
  clauseUid: string;
  previewOpen: boolean;
  onTogglePreview: () => void;
  onSelect: (clauseUid: string) => void;
}

const KIND_LABELS: Record<string, string> = {
  front_matter: "Front matter",
  section: "Section",
  heading: "Heading",
  clause: "Clause",
  item: "Item",
  appendix: "Appendix",
  schedule: "Schedule",
  glossary: "Glossary",
};

function DetailSection({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="mt-6" aria-label={title}>
      <h3 className="m-0 mb-2.5 flex items-center gap-2 text-xs font-semibold tracking-caps text-muted-foreground uppercase">
        {title}
        {count === undefined ? null : <span className="tabular-nums">{formatNumber(count)}</span>}
      </h3>
      {children}
    </section>
  );
}

export function OriginalWording({ text, language }: { text: string; language: string }) {
  return (
    <blockquote lang={language} className="m-0 mt-2 border-l-2 border-b3 pl-2.5 text-chip text-ink-2">
      <span className="sr-only">Original wording: </span>
      {text}
    </blockquote>
  );
}

function RequirementCard({
  requirement,
  region,
  language,
  internal,
}: {
  requirement: Requirement;
  region: string;
  language: string;
  internal: boolean;
}) {
  return (
    <li className="rounded-md border bg-card px-3.5 py-3">
      <div className="flex gap-3">
        <ValueTypeIcon valueType={requirement.value_type} labelled className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <RequirementLink
            reqUid={requirement.req_uid}
            region={region}
            className="text-ui font-medium text-foreground hover:text-brand-blue"
          >
            {requirement.statement}
          </RequirementLink>
          {isEnglish(language) || !requirement.source_text.trim() ? null : (
            <OriginalWording text={requirement.source_text} language={language} />
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="rounded-sm bg-muted px-1.5 py-0.5 text-xs font-semibold text-foreground tabular-nums">
              {requirementValue(requirement)}
            </span>
            <ModalityBadge modality={requirement.modality} />
            {internal ? <FlagBadge flags={requirement.quality_flags} /> : null}
          </div>
        </div>
      </div>
    </li>
  );
}

function ReferenceItem({
  reference,
  docUid,
  onSelect,
}: {
  reference: ReferenceRef;
  docUid: string;
  onSelect: (clauseUid: string) => void;
}) {
  if (reference.label === "ExternalStandard") {
    return (
      <li>
        <Badge variant="outline" title={reference.title ?? undefined}>
          {reference.designation ?? reference.uid}
        </Badge>
      </li>
    );
  }
  const number = reference.number ?? reference.uid.slice(reference.uid.indexOf("#") + 1);
  if (reference.uid.startsWith(`${docUid}#`)) {
    return (
      <li>
        <Button
          variant="outline"
          size="sm"
          className="h-7 rounded-pill font-mono text-xs"
          onClick={() => {
            onSelect(reference.uid);
          }}
        >
          {number}
        </Button>
      </li>
    );
  }
  return (
    <li>
      <ClauseLink clauseUid={reference.uid} number={number} />
    </li>
  );
}

function FigureCard({ figure, docUid, onOpen }: { figure: FigureRow; docUid: string; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-col gap-2 rounded-md border bg-card p-2 text-left transition-colors duration-150 hover:border-b3"
      >
        <FigureImage docUid={docUid} figureKey={figure.key} alt={figureTitle(figure)} dpi={110} />
        <span className="line-clamp-2 text-xs font-medium text-ink-2">{figureTitle(figure)}</span>
        <span className="text-label text-muted-foreground">
          p. {figure.page}
          {figure.curve_count > 0 ? ` · ${plural(figure.curve_count, "curve")}` : ""}
        </span>
      </button>
    </li>
  );
}

function TableItem({ table, docUid }: { table: TableRow; docUid: string }) {
  const caption = table.caption ?? `Table on page ${String(table.page)}`;
  return (
    <Collapsible className="rounded-md border">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-2 rounded-md bg-subtle px-3 py-2 text-left text-ui"
        >
          <ChevronRightIcon
            aria-hidden
            className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
          />
          <Table2Icon aria-hidden className="size-4 text-b2" />
          <span className="min-w-0 flex-1 truncate font-medium">{caption}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatNumber(table.n_rows)} × {formatNumber(table.n_cols)} · p. {table.page}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-3">
        <TableView docUid={docUid} tableKey={table.key} caption={caption} className="max-h-[420px]" />
      </CollapsibleContent>
    </Collapsible>
  );
}

function DetailSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-3 p-6">
      <Skeleton className="h-3.5 w-1/2" />
      <Skeleton className="h-7 w-2/3" />
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="mt-3 h-24 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

function ClauseHeader({
  detail,
  docUid,
  region,
  previewOpen,
  onTogglePreview,
  onSelect,
  internal,
}: {
  detail: ClauseDetailOut;
  docUid: string;
  region: string;
  previewOpen: boolean;
  onTogglePreview: () => void;
  onSelect: (clauseUid: string) => void;
  internal: boolean;
}) {
  const { clause } = detail;
  const href = `/${region}/library/${encodeURIComponent(docUid)}?tab=contents&clause=${encodeURIComponent(clause.clause_uid)}`;
  return (
    <header>
      {detail.path.length > 0 ? (
        <nav aria-label="Clause path">
          <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0 text-xs text-muted-foreground">
            {detail.path.map((ancestor) => (
              <li key={ancestor.clause_uid} className="inline-flex items-center gap-1">
                <button
                  type="button"
                  className="rounded-xs border-0 bg-transparent p-0 font-mono text-xs text-brand-blue hover:underline"
                  onClick={() => {
                    onSelect(ancestor.clause_uid);
                  }}
                >
                  {ancestor.number}
                </button>
                <ChevronRightIcon aria-hidden className="size-3 text-b3" />
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <div className="mt-1.5 flex flex-wrap items-start justify-between gap-3">
        <h2 className="m-0 min-w-0 text-ws font-bold tracking-h1 text-foreground">
          <span className="font-mono">{clause.number}</span>
          {clause.title ? <span className="ml-2 font-semibold">{clause.title}</span> : null}
          {clause.title && hasTranslation(clause.title_en, clause.language) ? (
            <span
              lang="en"
              className="mt-0.5 block text-ui font-medium tracking-normal text-muted-foreground"
            >
              {clause.title_en}
            </span>
          ) : null}
        </h2>
        <div className="flex shrink-0 items-center gap-1.5">
          <CopyButton value={absoluteUrl(href)} label="Copy link to this clause" />
          <Button asChild variant="outline" size="sm">
            <Link
              to="/$region/library/$docUid/pdf"
              params={{ region, docUid }}
              search={{ page: clause.page_start }}
            >
              <FileTextIcon aria-hidden />
              Open in PDF
            </Link>
          </Button>
          <Button variant="outline" size="sm" aria-pressed={previewOpen} onClick={onTogglePreview}>
            {previewOpen ? <PanelRightCloseIcon aria-hidden /> : <PanelRightOpenIcon aria-hidden />}
            {previewOpen ? "Hide source" : "Show source"}
          </Button>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        <Badge variant="outline">{KIND_LABELS[clause.kind] ?? clause.kind}</Badge>
        <Badge variant="outline">Level {clause.level}</Badge>
        <Badge variant="outline">{pageRange(clause.page_start, clause.page_end)}</Badge>
        {internal && clause.priority ? (
          <Badge variant={clause.priority === "P1" ? "brand" : "neutral"}>{clause.priority}</Badge>
        ) : null}
        {internal ? (
          <Badge variant="outline">
            <i aria-hidden className={cn("size-2 rounded-full", STATUS_DOT[clause.extraction_status])} />
            {EXTRACTION_STATUS_LABELS[clause.extraction_status]}
          </Badge>
        ) : null}
        {detail.section_title ? (
          <span className="text-xs text-muted-foreground">{detail.section_title}</span>
        ) : null}
        {internal ? <FlagBadge flags={clause.quality_flags} variant="pills" /> : null}
      </div>
    </header>
  );
}

export function ClauseDetail({
  docUid,
  region,
  clauseUid,
  previewOpen,
  onTogglePreview,
  onSelect,
}: ClauseDetailProps) {
  const internal = useInternalDetails();
  const query = useQuery(clausesGetClauseOptions({ path: { clause_uid: clauseUid } }));
  const [figure, setFigure] = useState<FigureRow | null>(null);
  const [bilingualMode] = useBilingualMode();
  if (query.isError) {
    return (
      <ProblemState
        problem={query.error}
        retry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (query.isPending) {
    return <DetailSkeleton />;
  }
  const detail = query.data;
  const { clause } = detail;
  const pending = internal && detail.requirements.length === 0 && clause.extraction_status === "pending";
  return (
    <article className="px-6 py-5" aria-label={`Clause ${clause.number}`}>
      <ClauseHeader
        detail={detail}
        docUid={docUid}
        region={region}
        previewOpen={previewOpen}
        onTogglePreview={onTogglePreview}
        onSelect={onSelect}
        internal={internal}
      />
      <div
        className={cn(
          "mt-4",
          bilingualMode === "side-by-side" && hasTranslation(clause.text_en, clause.language)
            ? "max-w-[164ch]"
            : "max-w-[78ch]",
        )}
      >
        {clause.text.trim().length > 0 ? (
          <BilingualText
            original={clause.text}
            english={clause.text_en}
            language={clause.language}
            renderOriginal={(text) => (
              <TermText
                text={text}
                docUid={docUid}
                definitions={detail.definitions.map((definition) => ({
                  definition_uid: definition.definition_uid,
                  term: definition.term,
                  text: definition.text,
                }))}
                references={detail.references}
                regionCode={region}
              />
            )}
          />
        ) : (
          <p className="m-0 text-ui text-muted-foreground">
            This clause is a heading without text of its own.
          </p>
        )}
      </div>
      {pending ? (
        <p className="m-0 mt-4 rounded-md bg-subtle px-3.5 py-2.5 text-ui text-ink-2">
          Not extracted yet (priority {clause.priority ?? "not set"}).
        </p>
      ) : null}
      {detail.requirements.length > 0 ? (
        <DetailSection title="Requirements" count={detail.requirements.length}>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {detail.requirements.map((requirement) => (
              <RequirementCard
                key={requirement.req_uid}
                requirement={requirement}
                region={region}
                language={clause.language}
                internal={internal}
              />
            ))}
          </ul>
        </DetailSection>
      ) : null}
      {detail.children.length > 0 ? (
        <DetailSection title="Contains" count={detail.children.length}>
          <ul className="m-0 list-none overflow-hidden rounded-md border p-0">
            {detail.children.map((child) => (
              <li key={child.clause_uid} className="border-b border-border-subtle last:border-b-0">
                <button
                  type="button"
                  onClick={() => {
                    onSelect(child.clause_uid);
                  }}
                  className="flex w-full items-center gap-2.5 border-0 bg-transparent px-3 py-2 text-left text-ui hover:bg-accent"
                >
                  {internal ? (
                    <i
                      aria-hidden
                      className={cn("size-2 shrink-0 rounded-full", STATUS_DOT[child.extraction_status])}
                    />
                  ) : null}
                  <span className="shrink-0 font-mono text-xs text-foreground">{child.number}</span>
                  {child.display_title !== child.number ? (
                    <span className="min-w-0 flex-1 truncate text-ink-2">{child.display_title}</span>
                  ) : (
                    <span className="flex-1" />
                  )}
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {child.requirement_count > 0 || child.subtree_requirement_count === 0
                      ? plural(child.requirement_count, "requirement")
                      : `${formatCompact(child.subtree_requirement_count)} below`}
                  </span>
                  <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-b3" />
                </button>
              </li>
            ))}
          </ul>
        </DetailSection>
      ) : null}
      {detail.figures.length > 0 ? (
        <DetailSection title="Figures" count={detail.figures.length}>
          <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2.5 p-0">
            {detail.figures.map((item) => (
              <FigureCard
                key={item.figure_uid}
                figure={item}
                docUid={docUid}
                onOpen={() => {
                  setFigure(item);
                }}
              />
            ))}
          </ul>
        </DetailSection>
      ) : null}
      {detail.tables.length > 0 ? (
        <DetailSection title="Tables" count={detail.tables.length}>
          <div className="flex flex-col gap-2">
            {detail.tables.map((table) => (
              <TableItem key={table.table_uid} table={table} docUid={docUid} />
            ))}
          </div>
        </DetailSection>
      ) : null}
      <div className="grid grid-cols-1 gap-x-6 2xl:grid-cols-2">
        <DetailSection
          title="References"
          count={detail.references.length + clause.unresolved_references.length}
        >
          {detail.references.length === 0 && clause.unresolved_references.length === 0 ? (
            <p className="m-0 text-ui text-muted-foreground">
              This clause refers to no other clause or standard.
            </p>
          ) : (
            <>
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                {detail.references.map((reference) => (
                  <ReferenceItem
                    key={reference.uid}
                    reference={reference}
                    docUid={docUid}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
              {clause.unresolved_references.length > 0 ? (
                <p className="m-0 mt-2 text-xs text-muted-foreground">
                  Not resolved: {clause.unresolved_references.join(", ")}
                </p>
              ) : null}
            </>
          )}
        </DetailSection>
        <DetailSection title="Referenced by" count={detail.referenced_by.length}>
          {detail.referenced_by.length === 0 ? (
            <p className="m-0 text-ui text-muted-foreground">No clause refers to this one.</p>
          ) : (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {detail.referenced_by.map((reference) => (
                <ReferenceItem
                  key={reference.uid}
                  reference={reference}
                  docUid={docUid}
                  onSelect={onSelect}
                />
              ))}
            </ul>
          )}
        </DetailSection>
        {hasHistory(detail.history) ? (
          <div className="2xl:col-span-2">
            <DetailSection title="In other versions">
              <ClauseVersions history={detail.history} />
            </DetailSection>
          </div>
        ) : null}
      </div>
      <FigureDialog
        docUid={docUid}
        region={region}
        figure={figure}
        onOpenChange={(open) => {
          if (!open) {
            setFigure(null);
          }
        }}
      />
    </article>
  );
}

export function NoClauseSelected() {
  return (
    <EmptyState
      icon={SquareDashedMousePointerIcon}
      title="Select a clause"
      description="Choose a clause in the table of contents to read its text, requirements, figures and references."
      className="h-full"
    />
  );
}
